(() => {
  const ROOT_SELECTOR = "[data-moz-cinematic-reveal]";
  const INSTANCE_KEY = "__moztechCinematicReveal";
  const VERSION = "1.1.0";

  if (window.MoztechCinematicProductReveal?.version) {
    window.MoztechCinematicProductReveal.scan(document);
    return;
  }

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const smoothstep = (start, end, value) => {
    const progress = clamp((value - start) / (end - start), 0, 1);
    return progress * progress * (3 - (2 * progress));
  };

  class CinematicReveal {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector("[data-moz-cr-track]");
      this.sticky = root.querySelector("[data-moz-cr-sticky]");
      this.caption = root.querySelector("[data-moz-cr-caption]");
      this.captionTrigger = root.querySelector(".moz-cinematic-reveal__identity") || this.caption;
      this.desktopVideo = root.querySelector("[data-moz-cr-video-desktop]");
      this.mobileVideo = root.querySelector("[data-moz-cr-video-mobile]");
      this.mobileQuery = window.matchMedia("(max-width: 749px)");
      this.reduceMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.isActive = false;
      this.frameRequest = 0;
      this.activeVideo = null;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);
      this.root[INSTANCE_KEY] = this;
      this.init();
    }

    init() {
      if (!this.track || !this.sticky) return;

      this.root.classList.add("is-motion-ready");

      this.stageObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.isActive = entry.isIntersecting;
            if (entry.isIntersecting) {
              this.loadActiveVideo();
              this.requestUpdate();
            } else if (this.activeVideo) {
              this.activeVideo.pause();
            }
          });
        },
        { rootMargin: "420px 0px 420px", threshold: 0 }
      );

      this.captionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.caption?.classList.toggle("is-visible", entry.isIntersecting);
          });
        },
        { rootMargin: "0px 0px -6%", threshold: 0.08 }
      );

      this.stageObserver.observe(this.track);
      if (this.captionTrigger) this.captionObserver.observe(this.captionTrigger);

      window.addEventListener("scroll", this.onScroll, { passive: true });
      window.addEventListener("resize", this.onResize, { passive: true });
      this.mobileQuery.addEventListener?.("change", this.onMediaChange);
      this.reduceMotionQuery.addEventListener?.("change", this.onMediaChange);

      this.requestUpdate();
    }

    getCurrentVideo() {
      return this.mobileQuery.matches ? this.mobileVideo : this.desktopVideo;
    }

    unloadVideo(video) {
      if (!video) return;
      video.pause();
      video.classList.remove("is-ready");
      video.querySelectorAll("source[src]").forEach((source) => {
        source.removeAttribute("src");
      });
      video.removeAttribute("src");
      video.dataset.loaded = "false";
      video.load();
    }

    loadActiveVideo() {
      if (this.reduceMotionQuery.matches) return;

      const nextVideo = this.getCurrentVideo();
      if (!nextVideo) return;

      if (this.activeVideo && this.activeVideo !== nextVideo) {
        this.unloadVideo(this.activeVideo);
      }

      this.activeVideo = nextVideo;

      if (nextVideo.dataset.loaded !== "true") {
        let hasSource = false;
        nextVideo.querySelectorAll("source[data-src]").forEach((source) => {
          if (!source.dataset.src) return;
          source.src = source.dataset.src;
          hasSource = true;
        });

        if (!hasSource) return;

        nextVideo.dataset.loaded = "true";
        nextVideo.load();
      }

      const markReady = () => nextVideo.classList.add("is-ready");
      if (nextVideo.readyState >= 2) {
        markReady();
      } else {
        nextVideo.addEventListener("loadeddata", markReady, { once: true });
      }

      const playPromise = nextVideo.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    }

    onScroll() {
      if (!this.isActive) return;
      this.requestUpdate();
    }

    onResize() {
      this.requestUpdate();
    }

    onMediaChange() {
      this.loadActiveVideo();
      this.requestUpdate();
    }

    requestUpdate() {
      if (this.frameRequest) return;
      this.frameRequest = window.requestAnimationFrame(() => {
        this.frameRequest = 0;
        this.update();
      });
    }

    update() {
      if (!this.track || !this.sticky) return;

      if (this.reduceMotionQuery.matches) {
        this.root.style.setProperty("--moz-cr-progress", "1");
        this.root.style.setProperty("--moz-cr-scale", "1");
        this.root.style.setProperty("--moz-cr-radius", "18px");
        this.root.style.setProperty("--moz-cr-frame-y", "0px");
        this.root.style.setProperty("--moz-cr-scroll-hint-opacity", "0");
        return;
      }

      const trackRect = this.track.getBoundingClientRect();
      const stickyHeight = this.sticky.getBoundingClientRect().height || window.innerHeight;
      const scrollRange = Math.max(1, this.track.offsetHeight - stickyHeight);
      const progress = clamp(-trackRect.top / scrollRange, 0, 1);
      const expansion = smoothstep(0.02, 0.68, progress);
      const isMobile = this.mobileQuery.matches;
      const startScale = Number.parseFloat(
        getComputedStyle(this.root).getPropertyValue(
          isMobile ? "--moz-cr-start-scale-mobile" : "--moz-cr-start-scale-desktop"
        )
      ) || (isMobile ? 0.88 : 0.82);
      const scale = startScale + ((1 - startScale) * expansion);
      const radius = (isMobile ? 22 : 28) * (1 - expansion);
      const frameY = (isMobile ? 1.5 : 3) * (1 - expansion);
      const hintOpacity = 1 - smoothstep(0, 0.3, progress);

      this.root.style.setProperty("--moz-cr-progress", progress.toFixed(4));
      this.root.style.setProperty("--moz-cr-scale", scale.toFixed(4));
      this.root.style.setProperty("--moz-cr-radius", `${radius.toFixed(2)}px`);
      this.root.style.setProperty("--moz-cr-frame-y", `${frameY.toFixed(3)}vh`);
      this.root.style.setProperty("--moz-cr-scroll-hint-opacity", hintOpacity.toFixed(3));
      this.root.toggleAttribute("data-expanded", progress > 0.9);
    }

    destroy() {
      this.stageObserver?.disconnect();
      this.captionObserver?.disconnect();
      window.removeEventListener("scroll", this.onScroll);
      window.removeEventListener("resize", this.onResize);
      this.mobileQuery.removeEventListener?.("change", this.onMediaChange);
      this.reduceMotionQuery.removeEventListener?.("change", this.onMediaChange);
      if (this.frameRequest) window.cancelAnimationFrame(this.frameRequest);
      this.unloadVideo(this.desktopVideo);
      if (this.mobileVideo !== this.desktopVideo) this.unloadVideo(this.mobileVideo);
      this.root.classList.remove("is-motion-ready");
      delete this.root[INSTANCE_KEY];
    }
  }

  const scan = (scope = document) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));

    roots.forEach((root) => {
      if (!root[INSTANCE_KEY]) new CinematicReveal(root);
    });
  };

  const destroy = (scope) => {
    const roots = [];
    if (scope?.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope?.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => root[INSTANCE_KEY]?.destroy());
  };

  window.MoztechCinematicProductReveal = { version: VERSION, scan, destroy };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => scan(document), { once: true });
  } else {
    scan(document);
  }

  document.addEventListener("shopify:section:load", (event) => scan(event.target));
  document.addEventListener("shopify:section:unload", (event) => destroy(event.target));
})();
